import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-my-family",
  title: "A prayer for my family — including the hard parts",
  h1: "A prayer for my family",
  description:
    "A prayer for the people you live with, four passages on households, and an honest word about praying for someone who is not changing.",
  targets: [
    "prayer for my family",
    "prayer for my children",
    "prayer for my household",
    "prayer for family problems",
  ],
  published: "2026-09-03",
  standfirst:
    "The people hardest to pray for honestly are usually the ones in the next room.",
  takeaways: [
    "Pray for them by name and for one specific thing each. A prayer for the family as a unit is a prayer for nobody in particular.",
    "Deuteronomy 6:7 locates the teaching in ordinary moments — sitting, walking, lying down — rather than in a scheduled religious slot.",
    "Praying for someone is not a technique for changing them, and treating it that way turns intercession into a way of being right about them.",
  ],
  when: [
    "In the car after everyone has got out.",
    "Outside the bedroom door of somebody who is not speaking to you.",
    "On the evening you shouted and were not entirely wrong and were not right either.",
    "Before a visit you are dreading.",
  ],
  prayer: [
    "God, these are the people you gave me, and I did not choose most of them, and some days that is the whole difficulty.",
    "Here is each of them by name, and one thing each that I would ask for if I could ask for only one.",
    "Keep the ones who are away. Be with the one who is struggling and will not say so. You know which one that is; I only think I do.",
    "Where I am the problem — where the atmosphere in this house is mine — show me that plainly and give me the humility to say so first.",
    "Let this be a house that is easy to come home to. Not a happy one necessarily. An easy one to come back to.",
  ],
  short:
    "God, these people, by name. Make this a house that is easy to come home to.",
  honest: [
    "This prayer does not guarantee outcomes for the people in it. Scripture contains a great many faithful parents whose children went their own way, and treating a prayer as a mechanism for producing obedience misunderstands both.",
    "It also does not fix a household by itself. If something is genuinely wrong — an addiction, a violence, a person who is not safe — the prayer belongs alongside real intervention rather than instead of it.",
    "And praying for someone is not the same as being right about them. Intercession curdles quickly into a way of cataloguing another person's faults in a voice that sounds like concern.",
  ],
  verses: [
    {
      verse: {
        text: "These words, which I command you today, shall be on your heart; and you shall teach them diligently to your children, and shall talk of them when you sit in your house, and when you walk by the way, and when you lie down, and when you rise up.",
        ref: "Deuteronomy 6:6–7",
      },
      note: "The order is the instruction: on your heart first, then taught. And the four settings listed are all ordinary and none of them are religious occasions — sitting about, walking somewhere, going to bed, getting up. It is a description of a household's normal conversation rather than of a family devotional slot.",
    },
    {
      verse: {
        text: "Unless Yahweh builds the house, they who build it labor in vain. Unless Yahweh watches over the city, the watchman guards it in vain.",
        ref: "Psalm 127:1",
      },
      note: "The next verse is about getting up early and staying up late eating the bread of toil, which places this squarely in the world of the anxious parent working too hard. It is not an argument against effort. It is an argument against effort carrying weight it cannot carry, which is a distinction most exhausted people need more than encouragement.",
    },
    {
      verse: {
        text: "You fathers, don't provoke your children to wrath, but nurture them in the discipline and instruction of the Lord.",
        ref: "Ephesians 6:4",
      },
      note: "Unusual for its time in addressing the person with the power rather than the child, and the first instruction is a prohibition on the parent. Provoke to wrath covers the ordinary domestic version — the sarcasm, the moved goalposts, the discipline that is really the parent's bad day — and it is named before anything is asked of the children.",
    },
    {
      verse: {
        text: "but as for me and my house, we will serve Yahweh.",
        ref: "Joshua 24:15",
      },
      note: "Quoted on a great many wall plaques, and the sentence before it is the part that gives it teeth: choose today whom you will serve. It is a public declaration made at a moment of genuine choice, by a man near the end of his life addressing people who had other options. Not a decoration — a decision, with alternatives listed.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "By name, one thing each",
      id: "by-name-one-thing-each",
    },
    {
      type: "p",
      text: "Praying for the family as a unit is comfortable and vague. Praying for each person by name, with one specific request, is neither — it forces you to decide what you actually want for them, which is often uncomfortable and occasionally revealing.",
    },
    {
      type: "p",
      text: "The revelation is usually about you. The thing you ask for a teenager will tell you quickly whether you want them to flourish or to be less inconvenient.",
    },
    {
      type: "h2",
      text: "The house has an atmosphere and somebody sets it",
      id: "the-house-has-an-atmosphere",
    },
    {
      type: "p",
      text: "In most households one person's mood determines the temperature of the room, and the person setting it is frequently the last to know. Ephesians 6:4 addresses that person first, before it asks anything of the children.",
    },
    {
      type: "p",
      text: "A great deal of what a family experiences as tension is arrangement rather than character — phones at the table, a rule nobody agreed to, an evening with no shape. Those are {fixable by agreement} in a way that personalities are not.",
      links: {
        "fixable by agreement": "/blog/phone-rules-for-christian-families",
      },
    },
    {
      type: "h2",
      text: "When somebody is not changing",
      id: "when-somebody-is-not-changing",
    },
    {
      type: "p",
      text: "The hardest version of this prayer is the one said for years about a person who is going the same direction regardless. It is where intercession most easily turns into a private inventory of somebody else's failings.",
    },
    {
      type: "note",
      text: "A useful discipline: for every request you make about them, make one about your own part in it. If you cannot find one, that is worth noticing.",
    },
  ],
  faq: [
    {
      q: "How should I pray for my family?",
      a: "By name, one specific request each. A prayer for the family as a whole is comfortable and asks for nothing in particular; naming people forces you to decide what you actually want for them.",
    },
    {
      q: "Which Bible verse is about family?",
      a: "Deuteronomy 6:6–7 for how faith actually passes on — in ordinary conversation while sitting, walking and going to bed — and Psalm 127:1 for the anxious parent working too hard.",
    },
    {
      q: "What do I pray when a family member has walked away from faith?",
      a: "Keep praying by name, and resist the urge to make the prayer an argument. Scripture has many faithful parents whose children went their own way; the prayer is not a mechanism for producing a result, and treating it as one damages the relationship it is meant to serve.",
    },
  ],
  relatedPrayers: ["prayer-for-patience", "prayer-for-anger"],
  related: ["phone-rules-for-christian-families", "digital-sabbath"],
  relatedTopics: ["bible-verses-about-peace"],
  relatedPassages: ["psalm-91"],
};
